import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, getDocs } from "firebase/firestore";
import useCurrentUser from "../hooks/useCurrentUser";

import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import {
  TrendingUp,
  MapPin,
  Clock,
  Package,
  CheckCircle,
  AlertCircle,
  MoreHorizontal,
  Truck
} from "lucide-react";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

// --- Components ---

function StatCard({ label, value, subtext, icon: Icon, colorClass }) {
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">{label}</p>
          <h3 className="text-3xl font-bold text-navy-900 mt-2">{value}</h3>
        </div>
        <div className={`p-3 rounded-full ${colorClass} bg-opacity-10`}>
          <Icon size={24} className={colorClass.replace("bg-", "text-")} />
        </div>
      </div>
      {subtext && <p className="text-xs text-slate-400 mt-4">{subtext}</p>}
    </div>
  );
}

function SectionHeader({ title, action }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <h3 className="text-lg font-bold text-navy-900 font-serif">{title}</h3>
      {action}
    </div>
  );
}

export default function ClientDashboard() {
  const { clientId, role, loading } = useCurrentUser();

  // ---------- state ----------
  const [shipments, setShipments] = useState([]);
  const [orderSummary, setOrderSummary] = useState({});
  const [search, setSearch] = useState("");
  const [payables, setPayables] = useState([]);
  const [receivables, setReceivables] = useState([]);

  // ---------- effects ----------
  useEffect(() => {
    if (!clientId || role !== "client") return;
    const unsub = onSnapshot(
      collection(db, "Clients", clientId, "shipments"),
      (snap) => setShipments(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("shipments error:", err)
    );
    return () => unsub();
  }, [clientId, role]);

  useEffect(() => {
    if (!clientId || role !== "client") return;
    let mounted = true;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "Clients", clientId, "orders"));
        if (!mounted) return;
        const summary = {};
        snap.forEach((doc) => {
          const data = doc.data() || {};
          const customer = data.customer || "Unknown";
          const value = (Array.isArray(data.items) ? data.items : []).reduce(
            (sum, it) => sum + Number(it?.totalValue || 0),
            0
          );
          if (!summary[customer]) summary[customer] = { count: 0, value: 0 };
          summary[customer].count += 1;
          summary[customer].value += value;
        });
        setOrderSummary(summary);
      } catch (e) {
        if (mounted) {
          console.error("orders summary error:", e);
          setOrderSummary({});
        }
      }
    })();
    return () => { mounted = false; };
  }, [clientId, role]);

  useEffect(() => {
    if (!clientId || role !== "client") return;

    const unsubPay = onSnapshot(
      collection(db, "Clients", clientId, "payables"),
      (snap) => setPayables(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("payables error:", err)
    );

    const unsubRec = onSnapshot(
      collection(db, "Clients", clientId, "receivables"),
      (snap) => setReceivables(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("receivables error:", err)
    );

    return () => {
      unsubPay();
      unsubRec();
    };
  }, [clientId, role]);

  // ---------- memos ----------
  const today = new Date();

  const filtered = useMemo(
    () => shipments.filter((s) => (s.id || "").toLowerCase().includes(search.toLowerCase())),
    [shipments, search]
  );

  const shipmentKPIs = useMemo(() => {
    const normStatus = (s) => (s?.status || "").toLowerCase();
    const inTransit = shipments.filter((s) =>
      ["confirmed", "booked", "in transit"].includes(normStatus(s))
    ).length;
    const arrived = shipments.filter((s) => normStatus(s) === "arrived").length;
    const delayed = shipments.filter((s) => normStatus(s) === "delayed").length;
    const etaThisWeek = shipments.filter((s) => {
      const eta = toJsDate(s.eta);
      if (!eta) return false;
      const in7 = new Date(today.getTime() + 7 * 86400000);
      return eta >= startOfDay(today) && eta <= endOfDay(in7);
    }).length;
    return { inTransit, arrived, delayed, etaThisWeek };
  }, [shipments, today]);

  const ordersChart = useMemo(() => {
    const labels = Object.keys(orderSummary);
    return {
      labels,
      data: {
        labels,
        datasets: [
          {
            label: "Orders",
            data: labels.map((k) => orderSummary[k]?.count || 0),
            backgroundColor: "#1F3A56", // Steel Blue
          },
          {
            label: "Total Value ($AUD)",
            data: labels.map((k) => orderSummary[k]?.value || 0),
            backgroundColor: "#D4AF37", // Gold
          },
        ],
      },
    };
  }, [orderSummary]);

  const financials = useMemo(() => {
    let openPayablesTotal = 0;
    const byType = new Map();

    payables.forEach((p) => {
      const amount = Number(p.amount || p.total || 0);
      const status = (p.status || "").toLowerCase();
      const type = (p.type || p.category || "Other") + "";

      if (status !== "paid") openPayablesTotal += amount;
      byType.set(type, (byType.get(type) || 0) + amount);
    });

    const outstandingReceivables = receivables
      .filter((r) => (r.status || "").toLowerCase() !== "paid")
      .reduce((s, r) => s + Number(r.amount || r.total || 0), 0);

    const pieLabels = Array.from(byType.keys());
    const pieValues = Array.from(byType.values()).map(round2);

    const overdueCount = payables.filter((p) => {
      const due = toJsDate(p.dueDate || p.due);
      const status = (p.status || "").toLowerCase();
      return status !== "paid" && due && due < startOfDay(today);
    }).length;

    return {
      openPayablesTotal: round2(openPayablesTotal),
      outstandingReceivables: round2(outstandingReceivables),
      pieLabels,
      pieValues,
      overdueCount,
    };
  }, [payables, receivables, today]);

  const pieData = useMemo(
    () => ({
      labels: financials.pieLabels,
      datasets: [
        {
          data: financials.pieValues,
          backgroundColor: ["#0B1F33", "#1F3A56", "#D4AF37", "#94a3b8", "#64748b"],
        },
      ],
    }),
    [financials.pieLabels, financials.pieValues]
  );

  // ---------- render ----------
  if (loading) return <div className="p-8 text-slate-500">Loading dashboard...</div>;
  if (!clientId || role !== "client") return <div className="p-8 text-red-500">Access denied.</div>;

  return (
    <div className="space-y-8 pb-10">
      {/* 4 KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          label="In Transit"
          value={shipmentKPIs.inTransit}
          icon={Truck}
          colorClass="bg-blue-600"
        />
        <StatCard
          label="Arrived (7 Days)"
          value={shipmentKPIs.arrived}
          icon={CheckCircle}
          colorClass="bg-emerald-600"
        />
        <StatCard
          label="Est. Arrival (This Week)"
          value={shipmentKPIs.etaThisWeek}
          icon={Clock}
          colorClass="bg-indigo-600"
        />
        <StatCard
          label="Delayed / Exceptions"
          value={shipmentKPIs.delayed}
          subtext="Requires attention"
          icon={AlertCircle}
          colorClass="bg-amber-500"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">

        {/* Left Column: Shipments Table */}
        <div className="xl:col-span-2 space-y-8">

          {/* Recent Shipments Table */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <h3 className="text-navy-900 font-bold font-serif">Active Shipments</h3>
              <input
                type="text"
                placeholder="Search Container ID..."
                className="text-sm border border-slate-300 rounded px-3 py-1.5 focus:outline-none focus:border-navy-900"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 text-slate-500 font-semibold uppercase tracking-wider text-xs">
                  <tr>
                    <th className="px-6 py-3">Shipment Ref</th>
                    <th className="px-6 py-3">Origin</th>
                    <th className="px-6 py-3">Destination</th>
                    <th className="px-6 py-3">ETA</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.slice(0, 5).map((s) => (
                    <tr key={s.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 font-medium text-navy-900">{s.id}</td>
                      <td className="px-6 py-4 text-slate-600">{s.origin || "—"}</td>
                      <td className="px-6 py-4 text-slate-600">{s.destination || "—"}</td>
                      <td className="px-6 py-4 text-slate-600">{formatETA(s.eta)}</td>
                      <td className="px-6 py-4">
                        <StatusBadge status={s.status} />
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button className="text-slate-400 hover:text-navy-900">
                          <MoreHorizontal size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan="6" className="px-6 py-8 text-center text-slate-400 italic">No shipments found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="bg-slate-50 px-6 py-3 border-t border-slate-200 text-right">
              <button className="text-sm font-semibold text-navy-900 hover:text-gold-600 transition-colors">
                View All Shipments &rarr;
              </button>
            </div>
          </div>

          {/* Orders Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <SectionHeader title="Order Volume by Customer" />
            <div className="aspect-[3/1]">
              {ordersChart.labels.length ? (
                <Bar
                  data={ordersChart.data}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: { grid: { color: '#f1f5f9' }, ticks: { font: { family: 'Inter' } } },
                      x: { grid: { display: false }, ticks: { font: { family: 'Inter' } } }
                    },
                    plugins: { legend: { position: "top", labels: { font: { family: 'Inter' } } } }
                  }}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">No order data available.</div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Financials */}
        <div className="space-y-6">
          {/* Financial Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <SectionHeader title="Financial Overview" />

            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded border border-slate-100">
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Total Payables</p>
                <div className="flex items-end justify-between">
                  <span className="text-2xl font-bold text-navy-900">{fmtAUD(financials.openPayablesTotal)}</span>
                  <span className="text-xs text-slate-400 mb-1">Due Now</span>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded border border-slate-100">
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Receivables</p>
                <div className="flex items-end justify-between">
                  <span className="text-2xl font-bold text-navy-900">{fmtAUD(financials.outstandingReceivables)}</span>
                  <span className="text-xs text-slate-400 mb-1">Outstanding</span>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <h4 className="text-sm font-bold text-navy-900 mb-4">Expense Breakdown</h4>
              <div className="h-48">
                {financials.pieValues.length ? (
                  <Pie
                    data={pieData}
                    options={{
                      plugins: { legend: { position: "right", labels: { boxWidth: 12, font: { size: 10 } } } },
                      maintainAspectRatio: false
                    }}
                  />
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">No expenses recorded.</div>
                )}
              </div>
            </div>
          </div>

          {/* Alerts */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <SectionHeader title="Action Items" />
            {financials.overdueCount > 0 ? (
              <ul className="space-y-3">
                {payables
                  .filter(p => {
                    const due = toJsDate(p.dueDate || p.due);
                    return (p.status || "").toLowerCase() !== "paid" && due && due < startOfDay(today);
                  })
                  .slice(0, 3)
                  .map(p => (
                    <li key={p.id} className="flex items-start gap-3 p-3 bg-red-50/50 rounded border border-red-100">
                      <AlertCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-navy-900">Overdue: {p.reference || p.id}</p>
                        <p className="text-xs text-red-600">{fmtAUD(p.amount || p.total)} &bull; Due {toJsDate(p.dueDate).toLocaleDateString()}</p>
                      </div>
                    </li>
                  ))}
              </ul>
            ) : (
              <div className="text-sm text-slate-500 flex items-center gap-2">
                <CheckCircle size={16} className="text-emerald-500" />
                All accounts are up to date.
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

// --- Helpers ---

function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  let classes = "bg-slate-100 text-slate-600";
  if (s === "arrived") classes = "bg-emerald-100 text-emerald-700";
  if (s === "in transit") classes = "bg-blue-100 text-blue-700";
  if (s === "delayed") classes = "bg-amber-100 text-amber-700";
  if (s === "customs hold") classes = "bg-red-100 text-red-700";

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${classes}`}>
      {status || "Pending"}
    </span>
  );
}

function toJsDate(v) {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  if (typeof v === "string" || typeof v === "number") {
    const d = new Date(v);
    return isNaN(d) ? null : d;
  }
  if (typeof v === "object") {
    if (typeof v.toDate === "function") return v.toDate();
    if ("seconds" in v) return new Date(v.seconds * 1000);
  }
  return null;
}
function startOfDay(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
function endOfDay(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999); }
function round2(n) { return Math.round(Number(n || 0) * 100) / 100; }
function fmtAUD(n) {
  try { return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(n || 0)); }
  catch { return `A$${Number(n || 0).toFixed(2)}`; }
}
function formatETA(etaLike) {
  const eta = toJsDate(etaLike);
  if (!eta) return "TBD";
  const diff = Math.ceil((startOfDay(eta) - startOfDay(new Date())) / 86400000);
  return diff > 0 ? `in ${diff} day(s)` : diff === 0 ? "Today" : "Arrived";
}
